﻿import React, { useState, useEffect } from 'react';
import LoadingIndicator from 'Components/Global/loading-indicator/loading-indicator';
import CustomModal from 'Components/Global/CustomModal/CustomModal';
import NotificationSnackbar from '../../components/Global/NotificationSnackbar/NotificationSnackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { Box, FormControlLabel, TableContainer, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';
import {
  UploadFile,
  GetCompanyListByAgency,
  GetEmployeesByCompanyID,
  GetSuffixDropDown,
  GetGenderDropDown,
  GetEmployeeTypes,
  InsertCompany,
  DeleteCompany,
  GetIssueAge,
  PrintRoster,
  PrintList,
  UpdateEmployee,
  InsertEmployee,
  DeleteEmployee,
  BulkUpdateEffective,
} from 'api/wpm.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import HelpIcon from '@mui/icons-material/Help';
import './wpm.scss';
import { fetchSignedURL } from '../../api/file';
import Dropzone from '../../components/Dropzone/Dropzone';

export default function WPM() {
  const queryClient = useQueryClient();
  //#region Variables
  let printEmployees = false;
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarStatus, setSnackbarStatus] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const handleCloseSnackbar = () => setSnackbarOpen(false);
  const showSuccessSnackbar = (msg) => { setSnackbarStatus('success'); setSnackbarMessage(msg); setSnackbarOpen(true); };
  const showErrorSnackbar = (msg) => { setSnackbarStatus('error'); setSnackbarMessage(msg); setSnackbarOpen(true); };
  const [companies, setCompanies] = useState(null);
  const [filteredCompanies, setFilteredCompanies] = useState(null);
  const [inputCompanyName, setInputCompanyName] = useState('');
  const [focusedCompany, setFocusedCompany] = useState(null);

  const [companyID, setCompanyID] = useState(null);
  const [focusedEmployee, setFocusedEmployee] = useState(null);
  const [companyEmployees, setCompanyEmployees] = useState(null);
  const [companyEmployeesToLink, setCompanyEmployeesToLink] = useState(null);
  const [filteredCompanyEmployees, setFilteredCompanyEmployees] = useState(null);
  const [showAddCompany, setShowAddCompany] = useState(false);

  const [showPrintCompany, setShowPrintCompany] = useState(false);
  const [showDeleteCompany, setShowDeleteCompany] = useState(false);

  const [showPrintSelectEmployees, setShowPrintSelectEmployees] = useState(false);
  const [selectedCompanyEmployees, setSelectedCompanyEmployees] = useState(null);
  const [showAddEmployee, setShowAddEmployee] = useState(false);

  const [showDeleteEmployee, setShowDeleteEmployee] = useState(false);

  const [employeeTypes, setEmployeeTypes] = useState(null);
  const [suffixDropDown, setSuffixDropDown] = useState(null);
  const [genderDropDown, setGenderDropDown] = useState(null);
  const [employeesToPrint, setEmployeesToPrint] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [clearUploadFiles, setClearUploadFiles] = useState(false);

  const [focusedCompanyID, setFocusedCompanyID] = useState(null);
  const [usingCompanyModal, setUsingCompanyModal] = useState(false);

  //#endregion

  //#region Employee Detail Vars
  const [gender, setGender] = useState(null);
  const handleChangeGender = (event) => {
    setGender(event.target.value);
  };

  const [employeeType, setEmployeeType] = useState(null);
  const handleChangeEmployeeType = (event) => {
    setEmployeeType(event.target.value);
  };

  const [suffix, setSuffix] = useState('');
  const handleChangeSuffix = (event) => {
    setSuffix(event.target.value);
  };

  const [dobDate, setDOBDate] = useState(new Date());
  const handleChangeDOBDate = (val) => {
    if (val instanceof Date && !isNaN(val)) setDOBDate(val);
  };

  const [effectiveDate, setEffectiveDate] = useState(new Date());
  const handleChangeEffectiveDate = (val) => {
    if (val instanceof Date && !isNaN(val)) setEffectiveDate(val);
  };

  const [showBulkEffectiveDate, setShowBulkEffectiveDate] = useState(false);
  const [bulkEffectiveDate, setBulkEffectiveDate] = useState(new Date());
  const handleBulkEffectiveDate = (val) => {
    setBulkEffectiveDate(val);
  };

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');

  const [linkID, setLinkID] = useState(0);
  const handleChangeLinkID = (event) => {
    setLinkID(event.target.value);
  };

  const GetFullName = (e) => {
    return e.FirstName + ' ' + e.MiddleName + ' ' + e.LastName + ' ' + e.Suffix;
  };

  const [issueAge, setIssueAge] = useState(0);

  function IsLoading() {
    if (
      getCompanyListByAgency.isFetching ||
      getGenderDropdown.isPending ||
      getEmployeeType.isPending ||
      getSuffixDropdown.isPending ||
      getEmployeesByCompanyID.isPending ||
      insertCompany.isPending ||
      bulkUpdateEffective.isPending ||
      deleteCompany.isPending ||
      deleteEmployee.isPending ||
      updateEmployee.isPending ||
      insertEmployee.isPending
    )
      return true;
    return false;
  }

  //#endregion

  //#region Server Calls

  function HideAllAndRefreshEmployees() {
    setShowAddEmployee(false);
    setShowDeleteEmployee(false);
    getEmployeesByCompanyID.mutate(
      { ID: companyID !== null && companyID !== undefined ? companyID : 0, Name: '', Agency: '' },
      {
        onSuccess: (result) => {
          let d = JSON.parse(result);
          for (let i = 0; i < d.length; i++) {
            d[i].DateOfBirth = format(new Date(d[i].DateOfBirth), 'MM/dd/yyyy');
            d[i].EffectiveDate = format(new Date(d[i].EffectiveDate), 'MM/dd/yyyy');
          }
          let sortedEmployees = sortEmployees(d);
          setCompanyEmployees(sortedEmployees);
          setFilteredCompanyEmployees(sortedEmployees);
          if (printEmployees == true) setShowPrintCompany(true);
        },
      }
    );
  }

  const printRoster = useQuery({
    queryKey: ['printRoster', focusedCompany?.ID],
    queryFn: () =>
      PrintRoster({
        CompanyID: focusedCompany?.ID,
        Data: '',
      })(),
    enabled: false,
  });

  useEffect(() => {
    if (printRoster.isSuccess && printRoster.data) {
      fetchSignedURL('WPMDocs/' + JSON.parse(printRoster.data));
      setShowPrintCompany(false);
      handleCloseEmployeesToPrintPrompt();
      showSuccessSnackbar('Roster generated');
    }
  }, [printRoster.isSuccess, printRoster.data]);

  useEffect(() => {
    if (printRoster.isError) showErrorSnackbar('Error generating roster');
  }, [printRoster.isError]);

  const printList = useQuery({
    queryKey: ['printList', focusedCompany?.ID],
    queryFn: () =>
      PrintList({
        CompanyID: focusedCompany?.ID,
        Data: employeesToPrint == '' ? JSON.stringify(focusedCompany?.ID) : employeesToPrint,
        IsCompany: employeesToPrint == '',
      })(),
    enabled: false,
  });

  useEffect(() => {
    if (printList.isSuccess && printList.data) {
      fetchSignedURL('WPMDocs/' + JSON.parse(printList.data));
      setShowPrintCompany(false);
      handleCloseEmployeesToPrintPrompt();
      showSuccessSnackbar('Print list generated');
      queryClient.resetQueries({ queryKey: ['printList', focusedCompany?.ID] });
    }
  }, [printList.isSuccess, printList.data]);

  useEffect(() => {
    if (printList.isError) showErrorSnackbar('Error generating print list');
  }, [printList.isError]);

  const updateEmployee = useMutation({
    mutationFn: () =>
      UpdateEmployee({
        ID: focusedEmployee?.ID,
        FirstName: firstName,
        MiddleName: middleName,
        LastName: lastName,
        Suffix: suffix == '' ? null : suffix,
        Gender: gender,
        DOB: dobDate,
        EffectiveDate: effectiveDate,
        IssueAge: issueAge,
        TypeID: employeeType == null ? 1 : employeeType,
        LinkID: linkID == 0 ? null : linkID,
        CreatedBy: '',
        ModifiedBy: '',
      })(),
  });

  const insertEmployee = useMutation({
    mutationFn: () =>
      InsertEmployee({
        ID: 0,
        CompanyID: focusedCompany?.ID,
        FirstName: firstName,
        MiddleName: middleName,
        LastName: lastName,
        Suffix: suffix == '' ? null : suffix,
        Gender: gender,
        DOB: dobDate,
        EffectiveDate: effectiveDate,
        IssueAge: issueAge,
        TypeID: employeeType == null ? 1 : employeeType,
        LinkID: linkID == 0 ? null : linkID,
        CreatedBy: null,
        ModifiedBy: null,
      })(),
  });

  const deleteEmployee = useMutation({
    mutationFn: () =>
      DeleteEmployee({ ID: focusedEmployee != null ? focusedEmployee.ID : 0 })(),
  });

  const getIssueAge = useQuery({
    queryKey: ['getIssueAge', 0],
    queryFn: () => GetIssueAge({ DateOfBirth: dobDate, EffectiveDate: effectiveDate })(),
    enabled: false,
    gcTime: 0,
  });

  useEffect(() => {
    if (getIssueAge.isSuccess && getIssueAge.data) {
      const parsed = JSON.parse(getIssueAge.data);
      if (typeof parsed === 'number') setIssueAge(parsed);
    }
  }, [getIssueAge.isSuccess, getIssueAge.data]);

  const getCompanyListByAgency = useQuery({
    queryKey: ['getCompanyList', 0],
    queryFn: () => GetCompanyListByAgency()(),
    enabled: false,
  });

  useEffect(() => {
    if (getCompanyListByAgency.isSuccess && getCompanyListByAgency.data) {
      let sortedCompanies = sortCompanies(JSON.parse(getCompanyListByAgency.data));
      setCompanies(sortedCompanies);
      setFilteredCompanies(sortedCompanies);
    }
  }, [getCompanyListByAgency.isSuccess, getCompanyListByAgency.data]);

  const insertCompany = useMutation({
    mutationFn: () => InsertCompany({ Name: inputCompanyName })(),
  });

  const getGenderDropdown = useQuery({
    queryKey: ['getGender', 0],
    queryFn: () => GetGenderDropDown()(),
  });

  useEffect(() => {
    if (getGenderDropdown.isSuccess && getGenderDropdown.data) {
      setGenderDropDown(JSON.parse(getGenderDropdown.data));
    }
  }, [getGenderDropdown.isSuccess, getGenderDropdown.data]);

  const getEmployeeType = useQuery({
    queryKey: ['getEmployeeType', 0],
    queryFn: () => GetEmployeeTypes()(),
  });

  useEffect(() => {
    if (getEmployeeType.isSuccess && getEmployeeType.data) {
      setEmployeeTypes(JSON.parse(getEmployeeType.data));
    }
  }, [getEmployeeType.isSuccess, getEmployeeType.data]);

  const getSuffixDropdown = useQuery({
    queryKey: ['getSuffix', 0],
    queryFn: () => GetSuffixDropDown()(),
  });

  useEffect(() => {
    if (getSuffixDropdown.isSuccess && getSuffixDropdown.data) {
      setSuffixDropDown(JSON.parse(getSuffixDropdown.data));
    }
  }, [getSuffixDropdown.isSuccess, getSuffixDropdown.data]);

  const deleteCompany = useMutation({
    mutationFn: () => DeleteCompany({ ID: focusedCompanyID })(),
  });

  const bulkUpdateEffective = useMutation({
    mutationFn: () =>
      BulkUpdateEffective({ CompanyID: focusedCompanyID, EffectiveDate: bulkEffectiveDate })(),
    onSuccess: () => {
      setShowBulkEffectiveDate(false);
      getEmployeesByCompanyID.mutate(
        { ID: focusedCompanyID, Name: '', Agency: '' },
        {
          onSuccess: (result) => {
            let d = JSON.parse(result);
            for (let i = 0; i < d.length; i++) {
              d[i].DateOfBirth = format(new Date(d[i].DateOfBirth), 'MM/dd/yyyy');
              d[i].EffectiveDate = format(new Date(d[i].EffectiveDate), 'MM/dd/yyyy');
            }
            let sortedEmployees = sortEmployees(d);
            setCompanyEmployees(sortedEmployees);
            setFilteredCompanyEmployees(sortedEmployees);
          },
        }
      );
      showSuccessSnackbar('Effective dates updated');
    },
    onError: () => showErrorSnackbar('Error updating effective dates'),
  });

  const getEmployeesByCompanyID = useMutation({
    mutationFn: (d) => GetEmployeesByCompanyID(d)(),
  });

  //#endregion

  //#region Filter Functions

  const sortCompanies = (c) => {
    let newList = c.sort(function (a, b) {
      if (a.CompanyName.toLowerCase() < b.CompanyName.toLowerCase()) return -1;
      if (a.CompanyName.toLowerCase() > b.CompanyName.toLowerCase()) return 1;
      return 0;
    });
    return newList;
  };

  const sortEmployees = (e) => {
    let newList = e.sort(function (a, b) {
      if (a.LastName.toLowerCase() < b.LastName.toLowerCase()) return -1;
      if (a.LastName.toLowerCase() > b.LastName.toLowerCase()) return 1;
      return 0;
    });
    return newList;
  };

  //#endregion

  //#region Styles

  const shortSpan = {
    width: '250px',
    display: 'inline',
  };

  const rowStyle = {
    padding: '1em 0 1em 1em',
  };

  //#endregion

  //#region UseEffect Functions

  useEffect(() => {
    RefreshCompanyListByAgency();
  }, []);
  useEffect(() => {
    getIssueAge.refetch();
  }, [dobDate, effectiveDate]);
  useEffect(() => {
    if (companyID != null) {
      getEmployeesByCompanyID.mutate(
        { ID: companyID !== null && companyID !== undefined ? companyID : 0, Name: '', Agency: '' },
        {
          onSuccess: (result) => {
            let d = JSON.parse(result);
            for (let i = 0; i < d.length; i++) {
              d[i].DateOfBirth = format(new Date(d[i].DateOfBirth), 'MM/dd/yyyy');
              d[i].EffectiveDate = format(new Date(d[i].EffectiveDate), 'MM/dd/yyyy');
            }
            let sortedEmployees = sortEmployees(d);
            setCompanyEmployees(sortedEmployees);
            setFilteredCompanyEmployees(sortedEmployees);
            if (printEmployees == true) setShowPrintCompany(true);
          },
        }
      );
    }
  }, [companyID]);
  useEffect(() => {
    if (focusedCompanyID != null) {
      setFocusedCompany(companies.filter((o) => o.ID == focusedCompanyID)[0]);
      setShowAddCompany(false);
      RefreshEmployeesByCompanyID(focusedCompanyID);
    }
  }, [focusedCompanyID]);

  //#endregion

  //#region Data Functions

  const AddCompany = () => {
    if (inputCompanyName != '') {
      insertCompany.mutate(
        {},
        {
          onSuccess: (data) => {
            setShowAddCompany(false);
            let parsedData = JSON.parse(data);
            RefreshCompanyListByAgency(parsedData[0].InsertedID);
            showSuccessSnackbar('Company added successfully');
          },
          onError: () => showErrorSnackbar('Error adding company'),
        }
      );
    }
  };

  const RemoveCompany = () => {
    if (focusedCompanyID != null) {
      deleteCompany.mutate(
        {},
        {
          onSuccess: () => {
            setShowDeleteCompany(false);
            RefreshCompanyListByAgency();
            showSuccessSnackbar('Company deleted successfully');
          },
          onError: () => showErrorSnackbar('Error deleting company'),
        }
      );
    }
  };

  const EmployeeRequest = (call) => {
    if (focusedCompany == null) {
      return;
    }
    switch (call) {
      case 'update':
        updateEmployee.mutate(
          {},
          {
            onSuccess: () => {
              HideAllAndRefreshEmployees();
              showSuccessSnackbar('Employee updated successfully');
            },
            onError: () => showErrorSnackbar('Error updating employee'),
          }
        );
        break;
      case 'insert':
        insertEmployee.mutate(
          {},
          {
            onSuccess: () => {
              HideAllAndRefreshEmployees();
              showSuccessSnackbar('Employee added successfully');
            },
            onError: () => showErrorSnackbar('Error adding employee'),
          }
        );
        break;
      case 'delete':
        deleteEmployee.mutate(
          {},
          {
            onSuccess: () => {
              HideAllAndRefreshEmployees();
              showSuccessSnackbar('Employee deleted successfully');
            },
            onError: () => showErrorSnackbar('Error deleting employee'),
          }
        );
        break;
    }
  };

  const formUploadMutate = useMutation({
    mutationFn: (data) => {
      let upload = UploadFile(data);
      return upload();
    },
  });

  const UploadBulkLoadTemplate = (files) => {
    setLoading(true);
    if (files.length > 0) {
      if (window.FormData !== undefined) {
        var data = new FormData();
        for (var x = 0; x < files.length; x++) {
          data.append('file' + x, files[x]);
          data.append('CompanyID', focusedCompany.ID);
          data.append('FileName', files[0].name);
          data.append('CreatedBy', '');
          data.append('Data', '');
        }
        formUploadMutate.mutate(data, {
          onSuccess: function () {
            RefreshEmployeesByCompanyID(focusedCompanyID);
            setLoading(false);
            handleCloseUploadModal();
            showSuccessSnackbar('File uploaded successfully');
          },
          onError: function () {
            setLoading(false);
            showErrorSnackbar('Error uploading file');
          },
        });
      } else {
        alert("This browser doesn't support HTML5 file uploads!");
      }
    }
  };

  const handleCloseUploadModal = () => {
    setClearUploadFiles((prev) => !prev);
    setUploadFiles([]);
    setUploadingFile(false);
  };

  const handleFilesAccepted = (files) => {
    setUploadFiles(files);
  };

  const handleUploadSubmit = () => {
    if (uploadFiles.length > 0) {
      UploadBulkLoadTemplate(uploadFiles);
      handleCloseUploadModal();
    }
  };

  const RefreshCompanyListByAgency = (selectCompany = null) => {
    ClosePrompt();
    setFocusedCompanyID(null);
    getCompanyListByAgency.refetch();
    setUsingCompanyModal(selectCompany != null);
    if (selectCompany != null) {
      setFocusedCompanyID(selectCompany);
    }
  };

  const RefreshEmployeesByCompanyID = (id, isPrint = false) => {
    printEmployees = isPrint;
    setCompanyID(id);
  };

  //#endregion

  //#region UI Functions

  const ClosePrompt = () => {
    setCompanyEmployees(null);
    setSelectedCompanyEmployees(null);
    setFocusedEmployee(null);
    setShowPrintSelectEmployees(false);
    printEmployees = false;
    setShowAddCompany(false);
    setShowPrintCompany(false);
  };

  const OpenEmployeePrompt = (e) => {
    setFocusedEmployee(e);
    setDOBDate(e != null ? new Date(e.DateOfBirth) : new Date());
    setEffectiveDate(e != null ? new Date(e.EffectiveDate) : new Date());
    setFirstName(e != null ? e.FirstName : '');
    setMiddleName(e != null ? e.MiddleName : '');
    setLastName(e != null ? e.LastName : '');
    setSuffix(e != null ? e.Suffix : '');
    setEmployeeType(e != null ? e.EmployeeTypeID : employeeTypes[0].ID);
    setLinkID(e != null ? e.EmployeeLinkID : 0);
    setGender(e != null ? e.Gender : genderDropDown[0].Value);
    setIssueAge(e != null ? e.IssueAge : 0);
    setShowAddEmployee(true);
  };

  const OpenDeleteEmployee = (e) => {
    setShowDeleteEmployee(true);
    setFocusedEmployee(e);
  };

  const OpenPrintCompany = (id) => {
    setShowPrintCompany(true);
    setFocusedCompanyID(id);
    getEmployeesByCompanyID.mutate(
      { ID: id, Name: '', Agency: '' },
      {
        onSuccess: (result) => {
          let d = JSON.parse(result);
          for (let i = 0; i < d.length; i++) {
            d[i].DateOfBirth = format(new Date(d[i].DateOfBirth), 'MM/dd/yyyy');
            d[i].EffectiveDate = format(new Date(d[i].EffectiveDate), 'MM/dd/yyyy');
          }
          let sortedEmployees = sortEmployees(d);
          setCompanyEmployees(sortedEmployees);
          setFilteredCompanyEmployees(sortedEmployees);
          if (printEmployees == true) setShowPrintCompany(true);
        },
      }
    );
  };

  const OpenDeleteCompany = (id) => {
    setShowDeleteCompany(true);
    setFocusedCompanyID(id);
  };

  const OpenPrintSelectEmployees = (company) => {
    printEmployees = true;
    RefreshEmployeesByCompanyID(company.ID, true);
    getEmployeesByCompanyID.mutate(
      { ID: company.ID, Name: '', Agency: '' },
      {
        onSuccess: (result) => {
          let d = JSON.parse(result);
          for (let i = 0; i < d.length; i++) {
            d[i].DateOfBirth = format(new Date(d[i].DateOfBirth), 'MM/dd/yyyy');
            d[i].EffectiveDate = format(new Date(d[i].EffectiveDate), 'MM/dd/yyyy');
          }
          let sortedEmployees = sortEmployees(d);
          setCompanyEmployees(sortedEmployees);
          setFilteredCompanyEmployees(sortedEmployees);
          if (printEmployees == true) setShowPrintCompany(true);
        },
      }
    );
    if (companyEmployees != null) {
      setSelectedCompanyEmployees(companyEmployees);
      setShowPrintSelectEmployees(true);
    }
  };

  const handleCloseEmployeesToPrintPrompt = () => {
    setEmployeesToPrint('');
    setShowPrintSelectEmployees(false);
    printEmployees = false;
  };

  const handleChangeSetEmployeesToPrint = (employee, isChecked) => {
    let id = JSON.stringify(employee.ID);
    let employees = '';
    let indexID = employeesToPrint.indexOf(id);
    if (isChecked.target.checked) {
      if (indexID == -1) {
        employees = employeesToPrint + (employeesToPrint != '' ? ',' + id : id);
        setEmployeesToPrint(employees);
      }
    } else {
      if (indexID != -1) {
        employees = employeesToPrint;
        let array = employees.split(',');
        let val = employees.replace(array.length > 1 ? ',' + id : id, '');
        setEmployeesToPrint(val);
      }
    }
  };

  const HandleEmployeePromptResponse = () => {
    if (focusedEmployee) EmployeeRequest('update');
    else EmployeeRequest('insert');
  };

  const CloseEmployeePrompt = () => {
    setShowAddEmployee(false);
    setFocusedEmployee(null);
    setSuffix('');
  };

  const HandleChangeEmployeeTypeResponse = (e) => {
    handleChangeEmployeeType(e);
    setCompanyEmployeesToLink(companyEmployees.filter((o) => o.EmployeeTypeID == 1));
  };

  const setCompanyFilter = (e) => {
    if ((e == null) | (e == '')) {
      setFilteredCompanies(companies);
      return;
    }
    var lst = companies.filter((o) => o.CompanyName.toLowerCase().indexOf(e.toLowerCase()) !== -1);
    setFilteredCompanies(lst);
  };

  const setEmployeeFilter = (e) => {
    if ((e == null) | (e == '')) {
      setFilteredCompanyEmployees(companyEmployees);
      return;
    }
    var lst = companyEmployees.filter(
      (o) => GetFullName(o).toLowerCase().indexOf(e.toLowerCase()) !== -1
    );
    setFilteredCompanyEmployees(lst);
  };

  const DisplayCompanyTable = () => {
    return (
      <div className="container">
        <div>
          <div className="row">
            <div className="col">&nbsp;</div>
          </div>
          <div className="row">
            <div className="col-3">
              <div style={{ display: 'inline' }}></div>
              <TextField
                style={{ ...shortSpan, float: 'left' }}
                placeholder="Employee Name Search..."
                onBlur={(e) => setEmployeeFilter(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setEmployeeFilter(e.target.value);
                  }
                }}
                variant="outlined"
                margin="dense"
              />
            </div>
            <div className="col-9 text-right">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setUploadingFile(true)}
              >
                Upload Bulk Template
              </button>
              &nbsp;&nbsp;
              <HelpIcon
                htmlColor={'#0074d9'}
                type="button"
                style={{ color: '#0074d9', margin: '0 0.5em 1em -0.5em' }}
                onClick={() => {
                  window.open('/WPMDocs/Template/WPM Help Guide.pdf');
                }}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowBulkEffectiveDate(true);
                }}
              >
                Update Effective Date
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => OpenEmployeePrompt(null)}
              >
                Add Employee
              </button>
              &nbsp;&nbsp;
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  RefreshCompanyListByAgency();
                }}
              >
                Back
              </button>
            </div>
          </div>
          <hr />
          {focusedCompany != null && (
            <div>
              <h2 style={{ textAlign: 'left' }}>{focusedCompany.CompanyName}</h2>
              {filteredCompanyEmployees != null && filteredCompanyEmployees.length > 0 && (
                <div className="row">
                  <div className="col">
                    <TableContainer>
                      <table className="table table-striped">
                        <thead>
                          <tr className="filteredEmployees__header">
                            <th>Employee Name</th>
                            <th>Gender</th>
                            <th>Date of Birth</th>
                            <th>Effective Date</th>
                            <th>Issue Age</th>
                            <th></th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCompanyEmployees.map((e, i) => {
                            return (
                              <tr key={i + 'row'} className="filteredEmployees__card">
                                <td>{GetFullName(e)}</td>
                                <td>{e.Gender}</td>
                                <td>{e.DateOfBirth}</td>
                                <td>{e.EffectiveDate}</td>
                                <td>{e.IssueAge}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                      OpenEmployeePrompt(e, true);
                                    }}
                                  >
                                    Edit
                                  </button>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => {
                                      OpenDeleteEmployee(e);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </TableContainer>
                  </div>
                </div>
              )}

              <>
                {focusedEmployee != null && (
                  <CustomModal
                    show={showDeleteEmployee}
                    onHide={() => {
                      setShowDeleteEmployee(false);
                    }}
                    title="Delete an Employee"
                    body={
                      <div>
                        Are you sure you want to delete employee {GetFullName(focusedEmployee)}?
                      </div>
                    }
                    buttonOne={
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={deleteEmployee.isPending}
                        onClick={() => {
                          EmployeeRequest('delete');
                        }}
                      >
                        {deleteEmployee.isPending ? <><CircularProgress size={14} />{' '}Deleting...</> : 'Yes'}
                      </button>
                    }
                    buttonTwo={
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={deleteEmployee.isPending}
                        onClick={() => {
                          setShowDeleteEmployee(false);
                        }}
                      >
                        No
                      </button>
                    }
                  />
                )}
              </>
            </div>
          )}
        </div>

        <CustomModal
          show={uploadingFile}
          onHide={handleCloseUploadModal}
          title="Upload Bulk Template"
          body={
            <Dropzone
              onFilesAccepted={handleFilesAccepted}
              maxFiles={1}
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
              }}
              dropzoneText="Drag & drop an Excel file here, or click to select"
              clearFiles={clearUploadFiles}
            />
          }
          buttonOne={
            <button type="button" className="btn btn-primary" disabled={loading} onClick={handleUploadSubmit}>
              {loading ? <><CircularProgress size={14} />{' '}Uploading...</> : 'Submit'}
            </button>
          }
          buttonTwo={
            <button type="button" className="btn btn-secondary" disabled={loading} onClick={handleCloseUploadModal}>
              Cancel
            </button>
          }
        />

        <CustomModal
          show={showBulkEffectiveDate}
          onHide={() => {
            setShowBulkEffectiveDate(false);
          }}
          title="Update Effective Date"
          body={
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div className="row" style={rowStyle}>
                <div className={'col'}>
                  <DatePicker
                    margin="normal"
                    id="bulkeffectivedate-dialog"
                    format="MM/dd/yyyy"
                    value={bulkEffectiveDate}
                    onChange={handleBulkEffectiveDate}
                    label="Effective Date"
                  />
                </div>
              </div>
            </LocalizationProvider>
          }
          buttonOne={
            <button
              type="button"
              className="btn btn-primary"
              disabled={bulkUpdateEffective.isPending}
              onClick={() => {
                bulkUpdateEffective.mutate();
              }}
            >
              {bulkUpdateEffective.isPending ? <><CircularProgress size={14} />{' '}Saving...</> : 'Save'}
            </button>
          }
          buttonTwo={
            <button
              type="button"
              className="btn btn-primary"
              disabled={bulkUpdateEffective.isPending}
              onClick={() => {
                setBulkEffectiveDate(new Date());
                setShowBulkEffectiveDate(false);
              }}
            >
              Cancel
            </button>
          }
        />

        <CustomModal
          show={showAddEmployee}
          onHide={() => {
            CloseEmployeePrompt();
          }}
          title={
            focusedCompany.CompanyName +
            ' — ' +
            (focusedEmployee != null ? 'Edit' : 'Add') +
            ' Employee'
          }
          body={
            <form>
              <div className="form-group">
                <div className="row" style={rowStyle}>
                  <div className={'col'}>
                    <TextField
                      placeholder="First Name..."
                      defaultValue={focusedEmployee != null ? firstName : ''}
                      onBlur={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="row" style={rowStyle}>
                  <div className={'col'}>
                    <TextField
                      placeholder="Middle Name..."
                      defaultValue={focusedEmployee != null ? middleName : ''}
                      onBlur={(e) => setMiddleName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="row" style={rowStyle}>
                  <div className={'col'}>
                    <TextField
                      placeholder="Last Name..."
                      defaultValue={focusedEmployee != null ? lastName : ''}
                      onBlur={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>
                {suffixDropDown != null && (
                  <div className="row" style={rowStyle}>
                    <div className={'col'}>
                      <FormControl style={{ minWidth: 200 }}>
                        <InputLabel id="suffix-simple-select-label">Suffix</InputLabel>
                        <Select
                          labelId="suffix-simple-select-label"
                          id="suffix-simple-select"
                          value={suffix}
                          onChange={handleChangeSuffix}
                          defaultValue={suffix}
                          label="Suffix"
                        >
                          {suffixDropDown.map((e, i) => {
                            return (
                              <MenuItem value={e.Value} key={i + 'dropdown'}>
                                {e.Text}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                )}

                {genderDropDown != null && (
                  <div className="row" style={rowStyle}>
                    <div className={'col'}>
                      <FormControl style={{ minWidth: 200 }}>
                        <InputLabel id="gender-simple-select-label">Gender</InputLabel>
                        <Select
                          labelId="gender-simple-select-label"
                          id="gender-simple-select"
                          value={gender}
                          onChange={handleChangeGender}
                          defaultValue={gender}
                          label="Gender"
                        >
                          {genderDropDown.map((e, i) => {
                            return (
                              <MenuItem value={e.Value} key={i + 'dropdown'}>
                                {e.Text}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                )}

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <div className="row" style={rowStyle}>
                    <div className={'col'}>
                      <DatePicker
                        margin="normal"
                        id="dob-dialog"
                        format="MM/dd/yyyy"
                        value={dobDate != null ? dobDate : new Date()}
                        onChange={handleChangeDOBDate}
                        label="Date of Birth"
                      />
                    </div>
                  </div>
                  <div className="row" style={rowStyle}>
                    <div className={'col'}>
                      <DatePicker
                        margin="normal"
                        id="effective-dialog"
                        format="MM/dd/yyyy"
                        value={effectiveDate != null ? effectiveDate : new Date()}
                        onChange={handleChangeEffectiveDate}
                        label="Effective Date"
                      />
                    </div>
                  </div>
                </LocalizationProvider>

                <div className="row" style={rowStyle}>
                  <div className={'col'}>
                    <label>Issue Age</label>
                    <div>{typeof issueAge === 'number' ? issueAge : ''}</div>
                  </div>
                </div>

                {employeeTypes != null && (
                  <div className="row" style={rowStyle}>
                    <div className={'col'}>
                      <FormControl style={{ minWidth: 200 }}>
                        <InputLabel id="employeeType-simple-select-label">Employee Type</InputLabel>
                        <Select
                          labelId="employeeType-simple-select-label"
                          id="employeeType-simple-select"
                          value={employeeType}
                          onChange={(e) => {
                            HandleChangeEmployeeTypeResponse(e);
                          }}
                          label="Employee Type"
                        >
                          {employeeTypes.map((e, i) => {
                            return (
                              <MenuItem value={e.ID} key={i + 'dropdown'}>
                                {e.TypeName}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                )}

                {employeeType != 1 && companyEmployeesToLink != null && (
                  <div className="row" style={rowStyle}>
                    <div className={'col'}>
                      <FormControl style={{ minWidth: 200 }}>
                        <InputLabel id="link-simple-select-label">Link ID</InputLabel>
                        <Select
                          labelId="link-simple-select-label"
                          id="link-simple-select"
                          value={linkID}
                          onChange={handleChangeLinkID}
                          label="Link ID"
                        >
                          {companyEmployeesToLink.map((e, i) => {
                            return (
                              <MenuItem value={e.ID} key={i + 'dropdown'}>
                                {GetFullName(e)}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                )}
              </div>
            </form>
          }
          buttonOne={
            <button
              type="button"
              className="btn btn-primary"
              disabled={updateEmployee.isPending || insertEmployee.isPending}
              onClick={() => {
                HandleEmployeePromptResponse();
              }}
            >
              {updateEmployee.isPending || insertEmployee.isPending
                ? <><CircularProgress size={14} />{' '}Saving...</>
                : 'Save'}
            </button>
          }
          buttonTwo={
            <button
              type="button"
              className="btn btn-primary"
              disabled={updateEmployee.isPending || insertEmployee.isPending}
              onClick={() => {
                CloseEmployeePrompt();
              }}
            >
              Cancel
            </button>
          }
        />
      </div>
    );
  };

  const DisplayListOfCompaniesTable = () => {
    return (
      <div className="container">
        {filteredCompanies != null && (
          <div>
            <div className="row">
              <div className="col">&nbsp;</div>
            </div>
            <div className="row">
              <div className="col">
                <TextField
                  style={{ ...shortSpan, float: 'left' }}
                  placeholder="Company Name Search..."
                  onBlur={(e) => setCompanyFilter(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setCompanyFilter(e.target.value);
                    }
                  }}
                  variant="outlined"
                  margin="dense"
                />
              </div>
              <div className="col text-right">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    fetchSignedURL('/WPMDocs/Template/WPM Employee Bulk Load Template.xlsx');
                  }}
                >
                  Download Bulk Template
                </button>
                &nbsp;&nbsp;
                <HelpIcon
                  htmlColor={'#0074d9'}
                  type="button"
                  style={{ color: '#0074d9', margin: '0 0.5em 1em -0.5em' }}
                  onClick={() => {
                    window.open('/WPMDocs/Template/WPM Help Guide.pdf');
                  }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowAddCompany(true);
                  }}
                >
                  Add
                </button>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col">
                <div>
                  <div className="filteredCompanies__header">
                    <Typography variant="h6">Company Name</Typography>
                  </div>
                  <hr />
                  {filteredCompanies.map((e, i) => (
                    <div key={i + 'row'} className="filteredCompanies__card">
                      <Typography className="filteredCompanies__title">
                        {e.CompanyName}
                      </Typography>
                      <div className="filteredCompanies__buttons">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            setFocusedCompanyID(e.ID);
                            setUsingCompanyModal(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            OpenPrintCompany(e.ID);
                            setUsingCompanyModal(false);
                          }}
                        >
                          Print
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => {
                            OpenDeleteCompany(e.ID);
                            setUsingCompanyModal(false);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <CustomModal
          show={showAddCompany}
          onHide={ClosePrompt}
          title="Add a Company"
          body={
            <form>
              <div className="form-group">
                <TextField
                  placeholder="Company Name..."
                  defaultValue=""
                  onBlur={(e) => setInputCompanyName(e.target.value)}
                />
              </div>
            </form>
          }
          buttonOne={
            <button
              type="button"
              className="btn btn-primary"
              disabled={insertCompany.isPending}
              onClick={() => {
                AddCompany();
              }}
            >
              {insertCompany.isPending ? <><CircularProgress size={14} />{' '}Submitting...</> : 'Submit'}
            </button>
          }
          buttonTwo={
            <button
              type="button"
              className="btn btn-primary"
              disabled={insertCompany.isPending}
              onClick={() => {
                setShowAddCompany(false);
              }}
            >
              Cancel
            </button>
          }
        />

        {focusedCompany != null && (
          <CustomModal
            show={showPrintCompany}
            onHide={() => {
              setShowPrintCompany(false);
              queryClient.resetQueries({ queryKey: ['printList', focusedCompany?.ID] });
            }}
            title={focusedCompany.CompanyName}
            body={
              companyEmployees === null || companyEmployees.length === 0 ? (
                <div style={{ textAlign: 'center', fontSize: '1em', width: '100%' }}>
                  <div>No employees exist for {focusedCompany.CompanyName}</div>
                </div>
              ) : null
            }
            buttonOne={
              companyEmployees !== null && companyEmployees.length > 0 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ minHeight: '5em' }}
                  disabled={printList.isFetching || printRoster.isFetching || getEmployeesByCompanyID.isPending}
                  onClick={() => {
                    printList.refetch();
                  }}
                >
                  {printList.isFetching ? <><CircularProgress size={14} />{' '}Generating...</> : 'All'}
                </button>
              ) : null
            }
            buttonTwo={
              companyEmployees !== null && companyEmployees.length > 0 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ minHeight: '5em' }}
                  disabled={printList.isFetching || printRoster.isFetching || getEmployeesByCompanyID.isPending}
                  onClick={() => {
                    OpenPrintSelectEmployees(focusedCompany);
                  }}
                >
                  Select Employee(s)
                </button>
              ) : null
            }
            buttonThree={
              companyEmployees !== null && companyEmployees.length > 0 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ minHeight: '5em' }}
                  disabled={printList.isFetching || printRoster.isFetching || getEmployeesByCompanyID.isPending}
                  onClick={() => {
                    printRoster.refetch();
                  }}
                >
                  {printRoster.isFetching ? <><CircularProgress size={14} />{' '}Generating...</> : 'Print Roster'}
                </button>
              ) : null
            }
          />
        )}

        {focusedCompany != null && (
          <CustomModal
            show={showDeleteCompany}
            onHide={() => {
              setShowDeleteCompany(false);
            }}
            title="Delete a Company"
            body={
              <div>
                Are you sure you want to <b>delete</b> {focusedCompany.CompanyName}? This action{' '}
                <b>cannot be undone</b> and will delete <b>all</b> employees.
              </div>
            }
            buttonOne={
              <button
                type="button"
                className="btn btn-danger"
                style={{ minHeight: '5em' }}
                disabled={deleteCompany.isPending}
                onClick={() => {
                  RemoveCompany();
                }}
              >
                {deleteCompany.isPending ? <><CircularProgress size={14} />{' '}Deleting...</> : 'Yes'}
              </button>
            }
            buttonTwo={
              <button
                type="button"
                className="btn btn-primary"
                style={{ minHeight: '5em' }}
                disabled={deleteCompany.isPending}
                onClick={() => {
                  setShowDeleteCompany(false);
                }}
              >
                No
              </button>
            }
          />
        )}

        <>
          {selectedCompanyEmployees != null && selectedCompanyEmployees.length > 0 && (
            <CustomModal
              show={showPrintSelectEmployees}
              onHide={handleCloseEmployeesToPrintPrompt}
              title={focusedCompany.CompanyName}
              body={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {selectedCompanyEmployees.map((e, i) => (
                    <FormControlLabel
                      key={i + 'row'}
                      control={
                        <Checkbox
                          color="primary"
                          onChange={(c) => handleChangeSetEmployeesToPrint(e, c)}
                        />
                      }
                      label={GetFullName(e)}
                    />
                  ))}
                </div>
              }
              buttonOne={
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={printList.isFetching}
                  onClick={() => {
                    printList.refetch();
                  }}
                >
                  {printList.isFetching ? <><CircularProgress size={14} />{' '}Generating...</> : 'Print'}
                </button>
              }
              buttonTwo={null}
              buttonThree={null}
            />
          )}
        </>
      </div>
    );
  };

  return (
    <div>
      <>
        {loading || IsLoading() ? (
          <LoadingIndicator />
        ) : (
          <>
            {focusedCompany != null && usingCompanyModal
              ? DisplayCompanyTable()
              : DisplayListOfCompaniesTable()}
          </>
        )}
        {(printList.isFetching || printRoster.isFetching) && (
          <div style={{ textAlign: 'center', margin: '5em', fontSize: 'large' }}>
            Generating the file...
          </div>
        )}
      </>
      <NotificationSnackbar
        duration={6000}
        status={snackbarStatus}
        openTrigger={snackbarOpen}
        onClose={handleCloseSnackbar}
        successMsg={snackbarMessage}
        errorMsg={snackbarMessage}
      />
    </div>
  );

  //#endregion
}
